/**
 * VehicleInspectionsPage — central de vistorias do tenant (Veículos > Vistoria).
 *
 * Modelo de página: "Contratos Gerados" (lista em /inspections, kanban em /inspections/kanban).
 *   - Lista: KPIs, filtros, tabela paginada; clique abre o modal de vistoria.
 *   - Kanban: colunas fixas por status, SEM arrastar e soltar (somente leitura),
 *     com carregamento incremental por coluna.
 * Permissões: rota protegida por PermissionGuard e validação no banco pela chave
 * da página (vehicles_inspections / vehicles_inspections_kanban).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-03 v1.9.0 - Exporta a tabela central para o detalhamento
 *            dos KPIs da aba Vistoria, sem duplicar paginação ou ordenação.
 *   RELEASE: 2026-10-02 v1.8.0 - Kanban abre em "Todos os períodos" (sem filtro de data,
 *            como Propostas) e guarda os filtros no navegador por usuário/empresa,
 *            restaurando-os ao voltar. Lista mantém o padrão do mês atual.
 *   RELEASE: 2026-10-01 v1.7.2 - Filtro rápido "Cliente" sai da barra do Kanban
 *            e passa a viver no Sheet "Mais filtros"; badge passa a somar cliente
 *            e tipo de responsável.
 *   RELEASE: 2026-10-01 v1.7.1 - Botão "Mais" do Kanban repadrado idêntico ao
 *            Kanban de Propostas (DealKanbanFilters): pill compacto, texto fixo
 *            "Mais" e badge de contagem com ml-0.5.
 *   RELEASE: 2026-10-01 v1.7.0 - Kanban: novo botão de filtros avançados (Sheet
 *            lateral) e o filtro "Tipo de responsável" (com complemento por
 *            usuário/fila/proprietário) sai da barra rápida para dentro dele.
 *   RELEASE: 2026-09-27 v1.6.0 - Lista alinhada a Propostas: navegador mensal,
 *            período por criação/conclusão, filtros identificados, ordenação,
 *            novas colunas e paginação compartilhada.
 *   RELEASE: 2026-09-26 v1.5.0 - Tipo de Checklist passa a usar filtro
 *            pesquisável compartilhado na Lista e no Kanban.
 *   RELEASE: 2026-09-26 v1.4.0 - Filtros compartilhados por Tipo de Checklist
 *            e Tipo de Responsável, com complemento opcional por responsável.
 *   RELEASE: 2026-09-26 v1.3.0 - Clique na placa abre o modal 360º do veículo
 *            vinculado (permissão vehicles_list); descrição em duas linhas no card.
 *   RELEASE: 2026-09-26 v1.2.0 - Card com proprietário histórico e filtros rápidos
 *            de cliente/contato e suspeita de fraude.
 *   RELEASE: 2026-09-26 v1.1.0 - Kanban alinhado ao padrão de Propostas,
 *            sem KPIs; lista reduzida para seis indicadores em uma linha.
 *   RELEASE: 2026-09-26 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import {
  AlertTriangle, Calendar, ClipboardCheck, Clock, LayoutGrid, List, Loader2,
  PlayCircle, RefreshCw, RotateCcw, Search, SlidersHorizontal, X, XCircle,
} from 'lucide-react';
import {
  ListPageLayout, ListPageHeader, ListPageKpis, KpiCard, ListPageFiltersBar, ListPageContent, ListPageFooter,
  SortableHeader, LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusDotLabel, TONE_DOT, type StatusDotTone } from '@/components/ui/status-dot-label';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { InspectionDialog } from '@/components/checklist/inspection/InspectionDialog';
import { ChecklistTypeFilter } from '@/components/checklist/filters/ChecklistTypeFilter';
import { InspectionKanbanBoard } from '@/components/vehicles/inspections/kanban/InspectionKanbanBoard';
// v1.3.0: modal 360º do veículo aberto pelo clique na placa do card.
import { VehicleDetailsModalPremium } from '@/components/vehicles/modal/VehicleDetailsModalPremium';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { useGlobalDistributionQueues } from '@/hooks/useChecklistTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import {
  useInspectionKpis, useInvalidateInspectionsKanban, useInspectionsListRealtime, useInspectionsPage,
  type InspectionDateField, type InspectionFilters, type InspectionListRow, type InspectionSortDirection,
  type InspectionSortField, type InspectionView,
} from '@/hooks/useChecklistInstancesList';
import type { PeriodFilter, PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import { cn } from '@/lib/utils';

/** Status da vistoria: rótulo + tom da bolinha (mesmo padrão do modal). */
const STATUS: Record<string, { label: string; tone: StatusDotTone }> = {
  pending: { label: 'Pendente', tone: 'neutral' },
  in_progress: { label: 'Em andamento', tone: 'info' },
  awaiting_review: { label: 'Aguardando revisão', tone: 'warning' },
  correction_requested: { label: 'Correção solicitada', tone: 'danger' },
  approved: { label: 'Aprovada', tone: 'success' },
  rejected: { label: 'Reprovada', tone: 'danger' },
  cancelled: { label: 'Cancelada', tone: 'neutral' },
};
const STATUS_ORDER = Object.keys(STATUS);

const DEADLINE: Record<string, string> = {
  on_time: 'Prazo livre', start_overdue: 'Início atrasado', finish_overdue: 'Conclusão atrasada', closed: 'Encerrada',
};

const ASSIGNEE_MODE_LABEL: Record<string, string> = {
  user: 'Usuário interno', queue: 'Fila global', owner: 'Cliente/proprietário', provider: 'Prestador',
};
const FREQUENCY_LABEL: Record<string, string> = {
  daily: 'Diária', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual',
};

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
type AssigneeModeFilter = 'all' | 'user' | 'queue' | 'owner' | 'provider';
/** v1.8.0: versão do formato salvo dos filtros do Kanban (muda = descarta o antigo). */
const KANBAN_FILTERS_VERSION = 1;

/** Placa no estilo Mercosul compacta. */
function Plate({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[11px] font-bold tracking-wider">
      {value}
    </span>
  );
}

function Progress({ row }: { row: InspectionListRow }) {
  const pct = row.items_total ? Math.round((row.items_answered / row.items_total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-muted-foreground tabular-nums">{row.items_answered}/{row.items_total}</span>
    </div>
  );
}

function FraudBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <Badge variant="outline" className="gap-1 border-destructive/30 bg-destructive/10 text-destructive text-[10px] px-1.5 py-0">
      <AlertTriangle className="h-3 w-3" />{count}
    </Badge>
  );
}

export default function VehicleInspectionsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canView } = usePermissions();
  const canList = canView('vehicles_inspections');
  const canKanban = canView('vehicles_inspections_kanban');
  // A visualização respeita a permissão: sem lista, cai direto no kanban (e vice-versa).
  // Rotas separadas para permitir PermissionGuard por página.
  const requested: InspectionView = location.pathname.endsWith('/kanban') ? 'kanban' : 'list';
  const view: InspectionView = requested === 'kanban' ? (canKanban ? 'kanban' : 'list') : (canList ? 'list' : 'kanban');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [status, setStatus] = useState<string>('all');
  const [deadline, setDeadline] = useState<string>('all');
  const [onlyFraud, setOnlyFraud] = useState(false);
  const [typeId, setTypeId] = useState('all');
  const [assigneeMode, setAssigneeMode] = useState<AssigneeModeFilter>('all');
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null);
  const [assigneeQueueId, setAssigneeQueueId] = useState('all');
  const [assigneeContactId, setAssigneeContactId] = useState<string | null>(null);
  const [assigneeCustomerId, setAssigneeCustomerId] = useState<string | null>(null);
  // v1.8.0: 'all' = sem filtro de data (somente no Kanban, que abre assim por padrão).
  const [dateField, setDateField] = useState<InspectionDateField | 'all'>(view === 'kanban' ? 'all' : 'created_at');
  const [monthFilter, setMonthFilter] = useState<Date>(new Date());
  const [period, setPeriod] = useState<PeriodFilter>({
    preset: 'month', startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()),
  });
  const [moreOpen, setMoreOpen] = useState(false);
  // v1.7.0: Sheet de filtros avançados exclusivo do Kanban.
  const [kanbanMoreOpen, setKanbanMoreOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  // Filtro e modal usam os IDs congelados no snapshot da vistoria, nunca o dono atual do veículo.
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerModal, setCustomerModal] = useState<{ customerId: string | null; contactId: string | null } | null>(null);
  const canOpenCustomers = canView('crm_customers');
  // v1.3.0: modal do veículo — o clique exige permissão de visualizar veículos.
  const [vehicleModalId, setVehicleModalId] = useState<string | null>(null);
  const canOpenVehicles = canView('vehicles_list');
  const { data: globalQueues = [] } = useGlobalDistributionQueues(assigneeMode === 'queue');

  /** Trocar o tipo remove complementos incompatíveis, mantendo o filtro somente pelo modo. */
  const changeAssigneeMode = (next: AssigneeModeFilter) => {
    setAssigneeMode(next);
    setAssigneeUserId(null);
    setAssigneeQueueId('all');
    setAssigneeContactId(null);
    setAssigneeCustomerId(null);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch(''); setStatus('all'); setDeadline('all'); setOnlyFraud(false); setTypeId('all');
    setSelectedContactId(null); setSelectedCustomerId(null); changeAssigneeMode('all');
    // v1.8.0: no Kanban, "Limpar" também volta para "Todos os períodos" (o efeito regrava o padrão).
    if (view === 'kanban') setDateField('all');
  };

  /** Mantém o navegador mensal e o período da consulta sincronizados. */
  const changeMonth = (nextMonth: Date) => {
    setMonthFilter(nextMonth);
    setPeriod({ preset: 'month', startDate: startOfMonth(nextMonth), endDate: endOfMonth(nextMonth) });
    setPage(1);
  };

  /** Resolve os mesmos atalhos de período utilizados em Propostas. */
  const changePeriodPreset = (preset: PeriodPreset) => {
    const now = new Date();
    const ranges: Record<Exclude<PeriodPreset, 'custom'>, { startDate: Date; endDate: Date }> = {
      today: { startDate: now, endDate: now }, yesterday: { startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1), endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1) },
      '7days': { startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6), endDate: now },
      '15days': { startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14), endDate: now },
      month: { startDate: startOfMonth(now), endDate: endOfMonth(now) },
      lastMonth: { startDate: startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1)), endDate: endOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1)) },
    };
    if (preset === 'custom') return;
    setPeriod({ preset, ...ranges[preset] });
    if (preset === 'month' || preset === 'lastMonth') setMonthFilter(ranges[preset].startDate);
    setPage(1);
  };

  // A listagem mantém seu canal; no Kanban a assinatura fica centralizada no board.
  useInspectionsListRealtime(view === 'list');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  // ---------------------------------------------------------------------------
  // v1.8.0: persistência local dos filtros do Kanban (por usuário + empresa).
  // A Lista não lê nem grava; a busca textual não é salva.
  // ---------------------------------------------------------------------------
  const { user, currentTenant } = useAuth();
  const storageKey = user?.id && currentTenant?.id
    ? `inspections-kanban-filters:${currentTenant.id}:${user.id}` : null;
  const hydratedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (view !== 'kanban') {
      hydratedKeyRef.current = null;
      // Lista não tem "Todos os períodos": volta ao padrão de criação.
      setDateField((current) => (current === 'all' ? 'created_at' : current));
      return;
    }
    if (!storageKey || hydratedKeyRef.current === storageKey) return;
    let saved: any = null;
    try { saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null'); } catch { saved = null; }
    if (saved && saved.v === KANBAN_FILTERS_VERSION) {
      const str = (val: unknown) => (typeof val === 'string' && val.length <= 100 ? val : null);
      setDeadline(str(saved.deadline) ?? 'all');
      setOnlyFraud(saved.onlyFraud === true);
      setTypeId(str(saved.typeId) ?? 'all');
      const mode = ['all', 'user', 'queue', 'owner', 'provider'].includes(saved.assigneeMode) ? saved.assigneeMode : 'all';
      setAssigneeMode(mode);
      setAssigneeUserId(str(saved.assigneeUserId));
      setAssigneeQueueId(str(saved.assigneeQueueId) ?? 'all');
      setAssigneeContactId(str(saved.assigneeContactId));
      setAssigneeCustomerId(str(saved.assigneeCustomerId));
      setSelectedContactId(str(saved.selectedContactId));
      setSelectedCustomerId(str(saved.selectedCustomerId));
      setDateField(['all', 'created_at', 'finished_at'].includes(saved.dateField) ? saved.dateField : 'all');
      // Presets relativos são recalculados; personalizado restaura as datas salvas.
      if (saved.preset === 'custom' && saved.startDate && saved.endDate) {
        const startDate = new Date(saved.startDate); const endDate = new Date(saved.endDate);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) setPeriod({ preset: 'custom', startDate, endDate });
      } else if (typeof saved.preset === 'string' && saved.preset !== 'custom') {
        try { changePeriodPreset(saved.preset as PeriodPreset); } catch { /* preset inválido: mantém padrão */ }
      }
    } else {
      setDateField('all');
    }
    hydratedKeyRef.current = storageKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, storageKey]);

  useEffect(() => {
    if (view !== 'kanban' || !storageKey || hydratedKeyRef.current !== storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        v: KANBAN_FILTERS_VERSION, deadline, onlyFraud, typeId, assigneeMode, assigneeUserId, assigneeQueueId,
        assigneeContactId, assigneeCustomerId, selectedContactId, selectedCustomerId, dateField,
        preset: period.preset, startDate: period.startDate.toISOString(), endDate: period.endDate.toISOString(),
      }));
    } catch { /* armazenamento indisponível: segue sem persistir */ }
  }, [view, storageKey, deadline, onlyFraud, typeId, assigneeMode, assigneeUserId, assigneeQueueId,
    assigneeContactId, assigneeCustomerId, selectedContactId, selectedCustomerId, dateField, period]);

  const filters: InspectionFilters = useMemo(() => ({
    search: debouncedSearch,
    status: view === 'list' && status !== 'all' ? [status] : null,
    deadlineStatus: deadline === 'all' ? null : deadline,
    onlyFraud,
    customerId: selectedCustomerId,
    contactId: selectedContactId,
    typeId: typeId === 'all' ? null : typeId,
    assigneeMode: assigneeMode === 'all' ? null : assigneeMode,
    assigneeUserId,
    assigneeQueueId: assigneeQueueId === 'all' ? null : assigneeQueueId,
    assigneeCustomerId,
    assigneeContactId,
    // v1.8.0: 'all' (ou Lista com valor herdado) vira consulta sem limite de data.
    dateField: dateField === 'all' ? 'created_at' : dateField,
    dateFrom: dateField === 'all' && view === 'kanban' ? null : format(period.startDate, 'yyyy-MM-dd'),
    dateTo: dateField === 'all' && view === 'kanban' ? null : format(period.endDate, 'yyyy-MM-dd'),
  }), [debouncedSearch, status, deadline, onlyFraud, selectedCustomerId, selectedContactId, typeId, assigneeMode, assigneeUserId, assigneeQueueId, assigneeCustomerId, assigneeContactId, dateField, period, view]);

  const hasFilters = !!search || status !== 'all' || deadline !== 'all' || onlyFraud || typeId !== 'all'
    || !!selectedContactId || !!selectedCustomerId || assigneeMode !== 'all'
    || (view === 'kanban' && dateField !== 'all');

  const assigneeSpecificFilter = (
    <>
      {assigneeMode === 'user' && (
        <div className="w-48 shrink-0"><SystemUserSelector value={assigneeUserId} onSelect={setAssigneeUserId} placeholder="Qualquer usuário" allowClear className="h-[22px] px-2 text-xs" /></div>
      )}
      {assigneeMode === 'queue' && (
        <Select value={assigneeQueueId} onValueChange={setAssigneeQueueId}>
          <SelectTrigger className="h-[22px] w-44 shrink-0 px-2 text-xs"><SelectValue placeholder="Qualquer fila" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Qualquer fila</SelectItem>{globalQueues.map((queue) => <SelectItem key={queue.id} value={queue.id}>{queue.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {assigneeMode === 'owner' && (
        <div className="w-52 shrink-0"><ContactSelector value={assigneeContactId} onSelect={setAssigneeContactId} onSelectContact={(contact) => setAssigneeCustomerId(contact?.customer_id ?? null)} placeholder="Qualquer proprietário" className="h-[22px] px-2 text-xs" /></div>
      )}
    </>
  );

  const { data: kpis } = useInspectionKpis('list', filters, view === 'list');
  const invalidateKanban = useInvalidateInspectionsKanban();

  const setView = (v: InspectionView) => {
    navigate(v === 'kanban' ? '/tenant/vehicles/inspections/kanban' : '/tenant/vehicles/inspections');
  };

  /** KPI clicável filtra a lista pelo status correspondente. */
  const kpiClick = (s: string) => () => { setStatus((cur) => (cur === s ? 'all' : s)); setPage(1); };

  if (view === 'kanban') {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        {/* Cabeçalho compacto no mesmo formato do Kanban de Propostas. */}
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-1 sm:shrink-0">
            <h1 className="shrink-0 text-xl font-semibold">Kanban Vistoria</h1>
            <div className="hidden items-center md:flex">
              <div className={cn(
                'overflow-hidden transition-all duration-300 ease-in-out',
                isSearchExpanded ? 'ml-2 mr-1 w-44 opacity-100' : 'w-0 opacity-0',
              )}>
                <div className="relative w-full">
                  <Input
                    placeholder="Buscar vistoria..."
                    value={search}
                    maxLength={100}
                    onChange={(event) => setSearch(event.target.value)}
                    onBlur={() => { if (!search) setIsSearchExpanded(false); }}
                    className="h-7 w-full rounded-full pl-3 pr-7 text-xs"
                    autoFocus={isSearchExpanded}
                  />
                  {search && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-0.5 top-1/2 h-6 w-6 -translate-y-1/2"
                      onClick={() => setSearch('')}
                      title="Limpar busca"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 rounded-full"
                onClick={() => setIsSearchExpanded((current) => (current && !search ? false : !current))}
                title="Buscar vistorias"
              >
                {isSearchExpanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto scrollbar-hide sm:gap-2">
            <Separator orientation="vertical" className="hidden h-5 shrink-0 sm:block" />
            <div className="relative w-40 shrink-0 md:hidden">
              <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={search}
                maxLength={100}
                onChange={(event) => setSearch(event.target.value)}
                className="h-[22px] w-full rounded-full pl-7 text-xs"
              />
            </div>
            <Select value={deadline} onValueChange={setDeadline}>
              <SelectTrigger className="h-[22px] max-w-[150px] shrink-0 px-2 text-xs">
                <SelectValue placeholder="Prazo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os prazos</SelectItem>
                {Object.entries(DEADLINE).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* RELEASE: 2026-10-01 - Kanban ganha campo de data e período (antes ficava preso ao mês atual). */}
            <Select value={dateField} onValueChange={(value) => { setDateField(value as InspectionDateField); setPage(1); }}>
              <SelectTrigger className="h-[22px] w-36 shrink-0 px-2 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos os períodos</SelectItem><SelectItem value="created_at">Data de criação</SelectItem><SelectItem value="finished_at">Data de conclusão</SelectItem></SelectContent>
            </Select>
            {/* v1.8.0: período só aparece quando há campo de data escolhido. */}
            {dateField !== 'all' && (
              <CrmPeriodFilter value={period} onPresetChange={changePeriodPreset} onCustomChange={(startDate, endDate) => { setPeriod({ preset: 'custom', startDate, endDate }); setPage(1); }} className="h-[22px] shrink-0 px-2 text-xs" />
            )}
            <ChecklistTypeFilter value={typeId} onChange={setTypeId} className="h-[22px] w-40 shrink-0 px-2 text-xs" />
            {/* v1.7.2: filtro rápido "Cliente" sai da barra e passa a viver no Sheet de filtros avançados. */}
            {/* v1.7.0: "Tipo de responsável" (e o complemento por usuário/fila/proprietário)
                sai da barra rápida e passa a viver no Sheet de filtros avançados. */}
            {/* v1.7.1: padrão idêntico ao botão "Mais filtros" do Kanban de Propostas (DealKanbanFilters). */}
            <Button
              variant="outline"
              size="sm"
              className="h-[22px] px-2 text-xs gap-1.5"
              onClick={() => setKanbanMoreOpen(true)}
              title="Mais filtros"
            >
              <SlidersHorizontal className="h-3 w-3" />
              <span>Mais</span>
              {(assigneeMode !== 'all' || selectedContactId) && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-0.5 bg-primary/15 text-primary border-0">
                  {(assigneeMode !== 'all' ? 1 : 0) + (selectedContactId ? 1 : 0)}
                </Badge>
              )}
            </Button>
            <Button
              variant={onlyFraud ? 'secondary' : 'outline'}
              size="sm"
              className={cn('h-[22px] shrink-0 px-2 text-xs', onlyFraud && 'text-destructive')}
              aria-pressed={onlyFraud}
              onClick={() => setOnlyFraud((current) => !current)}
              title="Mostrar somente vistorias com suspeita de fraude"
            >
              <AlertTriangle className="mr-1 h-3 w-3" />Suspeita de fraude
            </Button>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-[22px] shrink-0 px-2 text-xs text-muted-foreground"
                onClick={clearFilters}
              >
                <X className="mr-1 h-3 w-3" />Limpar
              </Button>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {canList && canKanban && (
              <div className="flex items-center rounded-md border p-0.5">
                <Button variant="ghost" size="icon-sm" onClick={() => setView('list')} title="Visualizar em lista">
                  <List className="h-3.5 w-3.5" />
                </Button>
                <Button variant="secondary" size="icon-sm" title="Visualizar em kanban por status">
                  <LayoutGrid className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            <Button variant="outline" size="icon-sm" onClick={invalidateKanban} title="Atualizar dados">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <InspectionKanbanBoard
            filters={filters}
            onOpen={setOpenId}
            canOpenCustomer={canOpenCustomers}
            onOpenCustomer={(inspection) => setCustomerModal({
              customerId: inspection.customer_id,
              contactId: inspection.contact_id,
            })}
            canOpenVehicle={canOpenVehicles}
            onOpenVehicle={(inspection) => {
              if (inspection.vehicle_id) setVehicleModalId(inspection.vehicle_id);
            }}
          />
        </div>
        <InspectionDialog instanceId={openId} onOpenChange={(open) => !open && setOpenId(null)} />
        <CustomerDetailsModalPremium
          customerId={customerModal?.customerId ?? null}
          contactId={customerModal?.contactId ?? null}
          open={!!customerModal}
          onOpenChange={(open) => { if (!open) setCustomerModal(null); }}
        />
        {/* v1.3.0: modal 360º do veículo, sem fechar a página nem o modal de vistoria. */}
        {/* v1.7.0: Sheet de filtros avançados do Kanban — mesmo padrão da Lista ("Mais filtros"). */}
        <Sheet open={kanbanMoreOpen} onOpenChange={setKanbanMoreOpen}>
          <SheetContent className="flex w-full flex-col sm:max-w-md">
            <SheetHeader>
              <SheetTitle>Mais filtros</SheetTitle>
              <SheetDescription>Filtre as vistorias por cliente, tipo de responsável e complemento.</SheetDescription>
            </SheetHeader>
            <div className="space-y-5 overflow-y-auto py-4">
              {/* v1.7.2: filtro de cliente movido da barra rápida para este Sheet (mesmo componente da Lista). */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Cliente</label>
                <ContactSelector
                  value={selectedContactId}
                  onSelect={setSelectedContactId}
                  onSelectContact={(contact) => setSelectedCustomerId(contact?.customer_id ?? null)}
                  placeholder="Cliente"
                  className="w-full h-8 px-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de responsável</label>
                <Select value={assigneeMode} onValueChange={(value) => changeAssigneeMode(value as AssigneeModeFilter)}>
                  <SelectTrigger><SelectValue placeholder="Tipo de responsável" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os responsáveis</SelectItem>
                    <SelectItem value="user">Usuário interno</SelectItem>
                    <SelectItem value="queue">Fila global</SelectItem>
                    <SelectItem value="owner">Cliente/proprietário</SelectItem>
                    <SelectItem value="provider">Prestador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Complemento por modo: usuário, fila ou proprietário (mesmos componentes da Lista). */}
              {assigneeSpecificFilter}
            </div>
          </SheetContent>
        </Sheet>

        <VehicleDetailsModalPremium
          vehicleId={vehicleModalId}
          open={!!vehicleModalId}
          onOpenChange={(open) => { if (!open) setVehicleModalId(null); }}
        />
      </div>
    );
  }

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Vistorias"
        center={(
          <MonthNavigator currentMonth={monthFilter} onChange={changeMonth} onClear={() => changeMonth(new Date())} />
        )}
        actions={canList && canKanban ? (
          <div className="inline-flex rounded-md border border-border p-0.5">
            <Button size="icon-sm" variant="secondary" onClick={() => setView('list')} title="Visualizar em lista">
              <List className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon-sm" variant="ghost" onClick={() => setView('kanban')} title="Visualizar em kanban">
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : undefined}
      />

      <ListPageKpis columns={6}>
        <KpiCard color="purple" icon={ClipboardCheck} label="Total" value={kpis?.total ?? 0} onClick={kpiClick('all')} active={status === 'all' && view === 'list'} />
        <KpiCard color="blue" icon={PlayCircle} label="Em andamento" value={kpis?.in_progress ?? 0} onClick={kpiClick('in_progress')} active={status === 'in_progress'} />
        <KpiCard color="yellow" icon={Clock} label="Aguardando revisão" value={kpis?.awaiting_review ?? 0} onClick={kpiClick('awaiting_review')} active={status === 'awaiting_review'} />
        <KpiCard color="orange" icon={RotateCcw} label="Correção solicitada" value={kpis?.correction_requested ?? 0} onClick={kpiClick('correction_requested')} active={status === 'correction_requested'} />
        <KpiCard color="red" icon={XCircle} label="Reprovadas" value={kpis?.rejected ?? 0} onClick={kpiClick('rejected')} active={status === 'rejected'} />
        <KpiCard
          color="cyan"
          icon={Clock}
          label="Atrasadas"
          value={kpis?.overdue ?? 0}
          onClick={() => {
            setDeadline((current) => current === 'finish_overdue' ? 'all' : 'finish_overdue');
            setPage(1);
          }}
          active={deadline === 'finish_overdue'}
        />
      </ListPageKpis>

      <ListPageFiltersBar
        onMoreClick={() => setMoreOpen(true)}
        moreCount={(deadline !== 'all' ? 1 : 0) + (onlyFraud ? 1 : 0) + (assigneeMode !== 'all' ? 1 : 0)}
        rightSlot={hasFilters ? <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters}><X className="mr-1 h-3.5 w-3.5" />Limpar</Button> : undefined}
      >
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, 'pl-7')}
            placeholder="Vistoria, veículo, cliente, CPF/CNPJ ou telefone..."
            maxLength={100}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        {/* RELEASE: 2026-09-27 v1.2.2 - Larguras dos filtros rápidos ampliadas para exibir o nome completo de cada filtro. */}
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[160px]')}><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
        <Select value={dateField} onValueChange={(value) => { setDateField(value as InspectionDateField); setPage(1); }}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[175px]')}>
            <div className="flex min-w-0 items-center gap-1.5 whitespace-nowrap"><Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><SelectValue /></div>
          </SelectTrigger>
          <SelectContent><SelectItem value="created_at">Data de criação</SelectItem><SelectItem value="finished_at">Data de conclusão</SelectItem></SelectContent>
        </Select>
        <CrmPeriodFilter value={period} onPresetChange={changePeriodPreset} onCustomChange={(startDate, endDate) => { setPeriod({ preset: 'custom', startDate, endDate }); setPage(1); }} className={cn(LIST_FILTERS_INPUT_CLASS, 'px-2', 'sm:min-w-[150px]')} />
        <ChecklistTypeFilter value={typeId} onChange={(value) => { setTypeId(value); setPage(1); }} className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[175px]')} />
      </ListPageFiltersBar>

      <InspectionsTable filters={filters} page={page} setPage={setPage} onOpen={setOpenId} onOpenCustomer={setCustomerModal} />

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent className="flex w-full flex-col sm:max-w-md">
          <SheetHeader><SheetTitle>Mais filtros</SheetTitle><SheetDescription>Refine as vistorias por prazo, fraude e responsável.</SheetDescription></SheetHeader>
          <div className="space-y-5 overflow-y-auto py-4">
            <div className="space-y-2"><label className="text-sm font-medium">Prazo</label><Select value={deadline} onValueChange={(v) => { setDeadline(v); setPage(1); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos os prazos</SelectItem>{Object.entries(DEADLINE).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><label className="text-sm font-medium">Tipo de responsável</label><Select value={assigneeMode} onValueChange={(value) => changeAssigneeMode(value as AssigneeModeFilter)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos os responsáveis</SelectItem><SelectItem value="user">Usuário interno</SelectItem><SelectItem value="queue">Fila global</SelectItem><SelectItem value="owner">Cliente/proprietário</SelectItem><SelectItem value="provider">Prestador</SelectItem></SelectContent></Select></div>
            {assigneeSpecificFilter}
            <Button variant={onlyFraud ? 'secondary' : 'outline'} className="w-full justify-start" onClick={() => { setOnlyFraud((current) => !current); setPage(1); }}><AlertTriangle className="mr-2 h-4 w-4" />Somente com suspeita de fraude</Button>
          </div>
        </SheetContent>
      </Sheet>

      <InspectionDialog instanceId={openId} onOpenChange={(o) => !o && setOpenId(null)} />
      <CustomerDetailsModalPremium customerId={customerModal?.customerId ?? null} contactId={customerModal?.contactId ?? null} open={!!customerModal} onOpenChange={(open) => { if (!open) setCustomerModal(null); }} />
    </ListPageLayout>
  );
}

// ------------------------------------------------------------------ Lista
export function InspectionsTable({ filters, page, setPage, onOpen, onOpenCustomer }: {
  filters: InspectionFilters; page: number; setPage: (p: number) => void; onOpen: (id: string) => void;
  onOpenCustomer: (value: { customerId: string | null; contactId: string | null }) => void;
}) {
  const [perPage, setPerPage] = useState(20);
  const [sortField, setSortField] = useState<InspectionSortField>('created_at');
  const [sortDirection, setSortDirection] = useState<InspectionSortDirection>('desc');
  const { data, isLoading } = useInspectionsPage('list', filters, page, perPage, sortField, sortDirection);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  /** Alterna a direção quando a coluna já está ativa. */
  const handleSort = (field: InspectionSortField) => {
    setSortDirection((current) => sortField === field && current === 'asc' ? 'desc' : 'asc');
    setSortField(field);
    setPage(1);
  };

  return (
    <>
      <ListPageContent>
        <ScrollIndicators className="h-full">
          <Table className="min-w-[1900px]" scrollViewport containerClassName="h-full">
            <TableHeader>
              <TableRow>
                {/* Barra lateral colorida pela situação (mesmo padrão da listagem de Propostas) */}
                <TableHead className="p-0 w-2" />
                <SortableHeader label="Código" field="code" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} className="w-20" />
                <SortableHeader label="Veículo" field="vehicle_label" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Cliente/contato" field="customer_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Template" field="template_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Tipo Checklist" field="checklist_type_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Status" field="status" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Prazo" field="due_finish_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Progresso" field="progress" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <TableHead>Alertas</TableHead>
                <SortableHeader label="Periodicidade" field="schedule_frequency" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Origem" field="origin" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Tipo Responsável" field="assignee_mode" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Responsável" field="assignee_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Criada em" field="created_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Conclusão em" field="finished_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={16} className="py-20 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></TableCell></TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={16} className="py-20 text-center text-sm text-muted-foreground">Nenhuma vistoria encontrada.</TableCell></TableRow>
              )}
              {/* RELEASE: 2026-09-27 v1.2.2 - Barra colorida lateral ao início da linha (padrão Propostas) e espaçamento py-2 idêntico ao de Propostas. */}
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => onOpen(r.id)}>
                  {/* Barra lateral colorida pela situação */}
                  <TableCell className="p-0 w-2">
                    <div className={cn('w-1 h-full min-h-[40px] rounded-full', TONE_DOT[STATUS[r.status]?.tone ?? 'neutral'])} />
                  </TableCell>
                  <TableCell className="py-2 font-mono text-xs">#{r.code}</TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2">
                      <Plate value={r.vehicle_plate} />
                      <span className="truncate max-w-[220px] text-xs text-muted-foreground">{r.vehicle_label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    <Button type="button" variant="ghost" className="h-auto justify-start gap-2 p-0 text-left font-normal hover:bg-transparent hover:underline" onClick={(event) => { event.stopPropagation(); onOpenCustomer({ customerId: r.customer_id, contactId: r.contact_id }); }}>
                      <SmartAvatar src={r.customer_avatar} name={r.customer_name ?? 'Cliente'} size="sm" />
                      <span className="max-w-[180px] truncate text-xs">{r.customer_name ?? '—'}</span>
                    </Button>
                  </TableCell>
                  {/* Template em linha única: nome + versão truncados, sem quebrar em múltiplas linhas. */}
                  <TableCell className="py-2 text-xs">
                    <div className="max-w-[220px] truncate">{r.template_name} <span className="text-muted-foreground">v{r.version_number}</span></div>
                  </TableCell>
                  <TableCell className="py-2 text-xs">{r.checklist_type_name ?? '—'}</TableCell>
                  <TableCell className="py-2"><StatusDotLabel tone={STATUS[r.status]?.tone ?? 'neutral'} label={STATUS[r.status]?.label ?? r.status} /></TableCell>
                  {/* Prazo em linha única: data + situação lateral, economiza altura. */}
                  <TableCell className="py-2 text-xs whitespace-nowrap">
                    <span>{fmtDate(r.due_finish_at)}</span>
                    <span className={cn('ml-2 text-[10px]', r.deadline_status.endsWith('overdue') ? 'text-destructive' : 'text-muted-foreground')}>
                      {DEADLINE[r.deadline_status] ?? r.deadline_status}
                    </span>
                  </TableCell>
                  <TableCell className="py-2"><Progress row={r} /></TableCell>
                  <TableCell className="py-2"><FraudBadge count={r.fraud_alerts} />{!r.fraud_alerts && <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="py-2 text-xs">{r.schedule_frequency ? (FREQUENCY_LABEL[r.schedule_frequency] ?? r.schedule_frequency) : '—'}</TableCell>
                  <TableCell className="py-2 text-xs">{r.origin === 'schedule' ? 'Automática' : 'Manual'}</TableCell>
                  <TableCell className="py-2 text-xs">{r.assignee_mode ? (ASSIGNEE_MODE_LABEL[r.assignee_mode] ?? r.assignee_mode) : '—'}</TableCell>
                  <TableCell className="py-2"><div className="flex items-center gap-2"><SmartAvatar src={r.executor_avatar} name={r.executor_name ?? 'Responsável'} size="sm" /><span className="max-w-[170px] truncate text-xs">{r.executor_name ?? '—'}</span></div></TableCell>
                  <TableCell className="py-2 text-xs">{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="py-2 text-xs">{fmtDate(r.finished_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollIndicators>
      </ListPageContent>
      <ListPageFooter>
        <PaginationFooter totalItems={total} currentPage={page} pageSize={perPage} onPageChange={setPage} onPageSizeChange={setPerPage} pageSizeOptions={[10, 20, 50, 100]} className="mt-0 w-full pt-0" borderClassName="border-0" />
      </ListPageFooter>
    </>
  );
}

